import { createContext, useState, useEffect, useRef, useContext } from "react";
import { UserContext } from "./user.context";
import { MessagesContext } from "./messages.context";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { downloadPDF } from "../utils/pdf/generate-pdf.js";
import { getApiBaseUrl } from "../utils/api-base-url.util";
import {
  GET_INVOICES_QUERY,
  GET_INVOICE_QUERY,
  GET_INVOICE_EXPORT_JOB_QUERY,
} from "../api/queries/invoice.queries";
import {
  GET_INVOICE_PDF,
  START_INVOICE_PDF_EXPORT,
} from "../api/mutations/invoice.mutations";

const TERMINAL_EXPORT_STATUSES = ["completed", "failed"];

export const InvoicesContext = createContext({
  invoices: [],
  setInvoices: () => null,
  fetchInvoices: () => null,
  fetchInvoice: () => null,
  downloadInvoicePDF: () => null,
  exportJob: null,
  startInvoiceExport: () => null,
  downloadExportResult: () => null,
  resetExportJob: () => null,
});

export const InvoicesProvider = ({ children }) => {
  const { addMessage } = useContext(MessagesContext);
  const { currentUser } = useContext(UserContext);
  const [invoices, setInvoices] = useState([]);
  const [getInvoicePDF] = useMutation(GET_INVOICE_PDF);
  const [getInvoice] = useLazyQuery(GET_INVOICE_QUERY);
  const [getInvoices, { data: getInvoicesData }] =
    useLazyQuery(GET_INVOICES_QUERY);

  const [startExportMutation] = useMutation(START_INVOICE_PDF_EXPORT);
  const [exportJob, setExportJob] = useState(null);
  const [exportJobId, setExportJobId] = useState(null);
  const autoDownloadedJobId = useRef(null);
  const { data: exportJobData, stopPolling: stopExportPolling } = useQuery(
    GET_INVOICE_EXPORT_JOB_QUERY,
    {
      variables: { _id: exportJobId },
      skip: !exportJobId,
      pollInterval: 2000,
      fetchPolicy: "network-only",
    }
  );

  useEffect(() => {
    const job = exportJobData?.invoiceExportJob;
    if (!job) return;
    setExportJob(job);
    if (TERMINAL_EXPORT_STATUSES.includes(job.status)) {
      stopExportPolling();
    }
    if (job.status === "completed" && autoDownloadedJobId.current !== job._id) {
      autoDownloadedJobId.current = job._id;
      downloadExportResult(job);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportJobData, stopExportPolling]);

  useEffect(() => {
    if (getInvoicesData?.invoices.length > 0) {
      setInvoices(getInvoicesData.invoices);
    }
  }, [getInvoicesData]);

  const fetchInvoices = async (sort, direction) => {
    try {
      if (!currentUser) return;
      const response = await getInvoices({
        variables: {
          customer_id: currentUser.customer_id,
          sort: `${direction === "asc" ? "" : "-"}${sort}`,
        },
        fetchPolicy: "network-only",
      });
      if (!response || response?.error) throw new Error(response.error);
      return response.data.invoices.map((invoice) => {
        return { ...invoice, ticket_number: invoice?.ticket?.number };
      });
    } catch (error) {
      addMessage(error.message, "error");
    }
  };

  const fetchInvoice = async (_id) => {
    try {
      if (!currentUser) return;
      const response = await getInvoice({
        variables: {
          _id,
        },
        fetchPolicy: "network-only",
      });
      if (!response || response?.error) throw new Error(response.error);
      return response?.data?.invoice;
    } catch (error) {
      addMessage(error.message, "error");
    }
  };

  const downloadInvoicePDF = async (_id, number) => {
    try {
      if (!currentUser || !_id) throw new Error("No user or invoice id");
      const response = await getInvoicePDF({
        variables: {
          _id,
        },
        fetchPolicy: "network-only",
      });
      if (!response || response?.error) throw new Error(response.error);
      downloadPDF(response?.data?.invoicePDF, `TCS Invoice - ${number}`);
    } catch (error) {
      addMessage(error.message, "error");
    }
  };

  const startInvoiceExport = async (invoiceIds) => {
    try {
      if (!currentUser || !invoiceIds?.length)
        throw new Error("No invoices selected");
      const response = await startExportMutation({
        variables: { invoice_ids: invoiceIds },
      });
      if (!response || response?.error) throw new Error(response.error);
      const job = response?.data?.startInvoicePDFExport;
      setExportJob(job);
      setExportJobId(job._id);
      return job;
    } catch (error) {
      addMessage(error.message, "error");
    }
  };

  const downloadExportResult = async (job = exportJob) => {
    try {
      if (!job?._id) throw new Error("No export ready to download");
      const response = await fetch(
        `${getApiBaseUrl()}/invoice-exports/${job._id}/download`,
        {
          credentials: "include",
          headers: {
            authorization: process.env.REACT_APP_TOKEN || "",
            token: sessionStorage.getItem("token") || "",
          },
        }
      );
      if (!response.ok) throw new Error("Failed to download export");
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = job.filename || "TCS-Invoices.pdf";
      link.click();
    } catch (error) {
      addMessage(error.message, "error");
    }
  };

  const resetExportJob = () => {
    setExportJobId(null);
    setExportJob(null);
  };

  const value = {
    invoices,
    setInvoices,
    fetchInvoices,
    fetchInvoice,
    downloadInvoicePDF,
    exportJob,
    startInvoiceExport,
    downloadExportResult,
    resetExportJob,
  };
  return (
    <InvoicesContext.Provider value={value}>
      {children}
    </InvoicesContext.Provider>
  );
};
